import React, { useRef } from 'react';
import Points from 'components/Points';
import { FoilCardImage } from 'components/FoilCardImage';
import { formatLanguages } from 'constants/languages';
import { CollectionList } from 'types/interfaces/collectionList';
import { getCardImageUrl, makeCardImageErrorHandler } from 'utils/cardImageFallback';
import { formatConditions, formatFoilPreference, shouldShowFoil } from 'utils/wantListHelpers';
import './OfferCardRow.css';

interface WantListInfoRowProps {
  /** A public want-list entry the viewer does NOT own */
  item: CollectionList;
  /** Batched calculate-price fill for entries without a displayPrice */
  calculatedPrice?: number;
  /**
   * Opens the "which copy do you own?" control. A sender who owns this card
   * but never catalogued it can add it here; the card is not blocked, only
   * un-listed. Absent (or with `addDisabledReason` set) the row keeps its
   * informational, non-addable form.
   */
  onAdd?: (item: CollectionList) => void;
  /** Why adding is unavailable right now, e.g. the 100-card side cap */
  addDisabledReason?: string;
  /** True once this entry has been added to the offer in this session */
  added?: boolean;
}

/**
 * Full-want-list row for an entry the viewer holds no have row for. Renders
 * fully (art, name, edition, condition, language, finish, price) at full
 * contrast — seeing what to acquire is the point of the mode.
 *
 * "Not in your collection" is a statement about the sender's HAVE LIST, not
 * about what they own: a sender part-way through cataloguing may well own the
 * card. So the row offers to add it (`onAdd`), which collects which copy they
 * own and creates the have row the offer line needs. Its displayed price stays
 * informational — the offer prices from the created row, never from here.
 */
const WantListInfoRow: React.FC<WantListInfoRowProps> = ({
  item,
  calculatedPrice,
  onAdd,
  addDisabledReason,
  added = false,
}) => {
  // Fallback step state lives in a ref, never DOM dataset (CLAUDE.md #15)
  const fallbackStepRef = useRef<Map<string | number, number>>(new Map());
  const name = item.cardName || item.card?.name || 'Unknown card';
  const imageInputs = {
    image: item.card?.imageUris?.normal || item.card?.normal_fallbacks?.[0] || null,
    scryfallId: item.card?.scryfallID || null,
  };
  const price = item.displayPrice ?? calculatedPrice ?? null;
  const editionLabel =
    item.isAnySet || !item.acceptableSetCodes || item.acceptableSetCodes.length === 0
      ? 'Any set'
      : `${item.acceptableSetCodes[0].toUpperCase()}${
          item.acceptableSetCodes.length > 1 ? ` +${item.acceptableSetCodes.length - 1}` : ''
        }`;

  return (
    <div className="offer-card-row offer-card-row--want-info">
      <div className="offer-card-art">
        {/* A foil-only WANT renders its art with the canonical overlay (any
            card image for a foil card gets the overlay, per the 2026-07-30
            ruling); the meta line's textual foil PREFERENCE label stays —
            that is a statement about the want, not a tag on the art. */}
        <FoilCardImage
          className="offer-card-art-foilwrap"
          src={getCardImageUrl(imageInputs)}
          alt={name}
          foil={shouldShowFoil(item)}
          loading="lazy"
          onError={makeCardImageErrorHandler(imageInputs, fallbackStepRef, item.id)}
        />
        {item.quantity > 1 && <div className="offer-card-art-qty">x{item.quantity}</div>}
      </div>
      <div className="offer-card-row-info">
        <div className="offer-card-row-name" title={name}>
          {name}
        </div>
        <div className="offer-card-row-meta">
          {editionLabel} · {formatConditions(item.acceptableConditions)} ·{' '}
          {formatLanguages(item.acceptableLanguages)} ·{' '}
          {formatFoilPreference(item.acceptFoil ?? true, item.acceptNonFoil ?? true)}
        </div>
        <div className="offer-card-row-price">
          {price != null ? (
            <Points type="small" points={price} />
          ) : (
            <span className="offer-card-row-noprice">No price data</span>
          )}
        </div>
      </div>
      <div className="offer-card-row-action offer-card-row-action--stacked">
        <button
          type="button"
          className="offer-add-btn"
          disabled={!onAdd || !!addDisabledReason || added}
          title={addDisabledReason}
          onClick={onAdd ? () => onAdd(item) : undefined}
        >
          {added ? 'Added' : 'Add'}
        </button>
        <span className="offer-row-reason">
          {added
            ? 'Added to your have list'
            : addDisabledReason
              ? addDisabledReason
              : onAdd
                ? 'Not on your have list yet'
                : 'Not in your collection'}
        </span>
      </div>
    </div>
  );
};

export default WantListInfoRow;
